/**
 * CDN Fallback Utility
 * Attempts to fetch from CDN with automatic fallback to local server on failures.
 * Detects and reports HTTP errors (especially 504s) to monitoring system.
 */

interface FetchOptions extends RequestInit {
  cdnUrl?: string;
  fallbackUrl?: string;
  timeout?: number;
  retries?: number;
  reportErrors?: boolean;
}

type CdnEndpointKind = "error" | "alert";

interface FetchResult<T> {
  data: T | null;
  ok: boolean;
  status: number;
  source: 'cdn' | 'fallback' | 'error';
  error?: Error;
  cdnFailed?: boolean;
}

// Track CDN status globally
const cdnStatus = {
  isFailing: false,
  lastFailure: 0,
  failureCount: 0,
  lastError: null as any,
  forcedOffline: false,
};

/**
 * Fetch with timeout support
 */
function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeout: number = 10000
): Promise<Response> {
  return Promise.race([
    fetch(url, options),
    new Promise<Response>((_, reject) =>
      setTimeout(() => reject(new Error(`Fetch timeout after ${timeout}ms`)), timeout)
    ),
  ]);
}

function readMetaContent(name: string): string {
  if (typeof document === "undefined") return "";
  const el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  return el?.content?.trim() || "";
}

function absoluteEndpoint(url: string): string {
  if (!url) return "";
  try {
    return new URL(url, typeof window !== "undefined" ? window.location.href : "http://localhost").toString();
  } catch {
    return url;
  }
}

export function resolveCdnReportingEndpoint(kind: CdnEndpointKind): string {
  const win = typeof window !== "undefined" ? (window as any) : {};
  const specificGlobal =
    kind === "error" ? win.__STORYCORE_CDN_ERROR_URL__ : win.__STORYCORE_CDN_ALERT_URL__;
  if (specificGlobal) return absoluteEndpoint(String(specificGlobal));

  const specificMeta =
    kind === "error"
      ? readMetaContent("storycore-cdn-error-url")
      : readMetaContent("storycore-cdn-alert-url");
  if (specificMeta) return absoluteEndpoint(specificMeta);

  const workerBase = win.__STORYCORE_CDN_WORKER_URL__ || readMetaContent("storycore-cdn-worker-url");
  if (workerBase) {
    return `${String(workerBase).replace(/\/$/, "")}/cdn-${kind}`;
  }

  const baseUrl = win.STORYCORE_BASE_URL || "/";
  return `${String(baseUrl).replace(/\/$/, "")}/api/cdn-${kind}`;
}

/**
 * Core fetch function with CDN fallback logic
 */
export async function fetchWithFallback<T = any>(
  url: string,
  options: FetchOptions = {}
): Promise<FetchResult<T>> {
  const {
    cdnUrl,
    fallbackUrl = url,
    timeout = 10000,
    retries = 1,
    reportErrors = true,
    ...fetchOpts
  } = options;

  // Try CDN first if available
  if (cdnUrl && !cdnStatus.isFailing && !cdnStatus.forcedOffline) {
    try {
      const response = await fetchWithTimeout(cdnUrl, fetchOpts, timeout);

      if (response.ok) {
        const data = await parseResponse<T>(response);
        return { data, ok: true, status: response.status, source: 'cdn' };
      }

      // Handle non-OK responses (including 504s)
      if (response.status >= 500) {
        handleCdnError(
          `CDN returned ${response.status}`,
          response.status,
          cdnUrl,
          reportErrors
        );

        // Try fallback
        return fetchFromFallback<T>(fallbackUrl, fetchOpts, timeout);
      }

      // For other non-OK responses, still try fallback
      return fetchFromFallback<T>(fallbackUrl, fetchOpts, timeout);
    } catch (err) {
      handleCdnError(
        `CDN fetch failed: ${err instanceof Error ? err.message : String(err)}`,
        0,
        cdnUrl,
        reportErrors
      );

      // Fall back to local server
      return fetchFromFallback<T>(fallbackUrl, fetchOpts, timeout);
    }
  }

  // If CDN is disabled or not configured, use fallback
  return fetchFromFallback<T>(fallbackUrl, fetchOpts, timeout);
}

/**
 * Fetch from fallback URL (local server)
 */
async function fetchFromFallback<T>(
  url: string,
  options: RequestInit,
  timeout: number
): Promise<FetchResult<T>> {
  for (let attempt = 0; attempt <= 1; attempt++) {
    try {
      const response = await fetchWithTimeout(url, options, timeout);

      if (response.ok) {
        const data = await parseResponse<T>(response);
        return { data, ok: true, status: response.status, source: 'fallback' };
      }

      // Last attempt - return error
      if (attempt === 1) {
        return {
          data: null,
          ok: false,
          status: response.status,
          source: 'error',
          cdnFailed: cdnStatus.isFailing,
        };
      }
    } catch (err) {
      // Last attempt - return error
      if (attempt === 1) {
        return {
          data: null,
          ok: false,
          status: 0,
          source: 'error',
          error: err instanceof Error ? err : new Error(String(err)),
          cdnFailed: cdnStatus.isFailing,
        };
      }
    }
  }

  return {
    data: null,
    ok: false,
    status: 0,
    source: 'error',
    cdnFailed: cdnStatus.isFailing,
  };
}

/**
 * Parse response based on content type
 */
async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return (await response.json()) as T;
  } else if (contentType.includes('text/')) {
    return (await response.text()) as T;
  } else {
    return (await response.blob()) as T;
  }
}

/**
 * Handle and report CDN errors
 */
function handleCdnError(
  message: string,
  status: number,
  cdnUrl: string,
  reportErrors: boolean
): void {
  cdnStatus.isFailing = true;
  cdnStatus.lastFailure = Date.now();
  cdnStatus.failureCount++;
  cdnStatus.lastError = { message, status, cdnUrl, timestamp: new Date() };

  console.error(`[CDN FALLBACK] ${message} (${status}) - URL: ${cdnUrl}`);

  if (reportErrors) {
    reportCdnError(message, status, cdnUrl);
  }
}

export function markCdnFailure(
  message: string,
  status: number,
  cdnUrl: string,
  reportErrors: boolean = true
): void {
  handleCdnError(message, status, cdnUrl, reportErrors);
}

/**
 * Report error to monitoring endpoint
 */
async function reportCdnError(
  message: string,
  status: number,
  cdnUrl: string
): Promise<void> {
  try {
    const endpoint = resolveCdnReportingEndpoint("error");

    await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        status,
        cdnUrl,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      }),
    }).catch((err) => {
      console.error('[CDN FALLBACK] Failed to report error:', err);
    });
  } catch (err) {
    console.error('[CDN FALLBACK] Error reporting failed:', err);
  }
}

/**
 * Get current CDN status
 */
export function getCdnStatus() {
  return { ...cdnStatus };
}

export function getCdnStatusMessage(): string {
  if (cdnStatus.forcedOffline) {
    return "CDN is forced offline for testing; local fallback is active.";
  }
  if (cdnStatus.isFailing) {
    const last = cdnStatus.lastError?.message || "Unknown CDN failure";
    return `CDN is failing. Local fallback is active. Last error: ${last}`;
  }
  return "CDN is healthy.";
}

export function setCdnOffline(offline: boolean): void {
  cdnStatus.forcedOffline = offline;
  if (offline) {
    cdnStatus.isFailing = true;
    cdnStatus.lastFailure = Date.now();
    cdnStatus.failureCount++;
    cdnStatus.lastError = {
      message: "CDN forced offline for testing",
      status: 0,
      cdnUrl: "",
      timestamp: new Date(),
    };
    return;
  }

  cdnStatus.isFailing = false;
  cdnStatus.lastError = null;
}

/**
 * Reset CDN status (call after CDN issue is resolved)
 */
export function resetCdnStatus(): void {
  cdnStatus.isFailing = false;
  cdnStatus.failureCount = 0;
  cdnStatus.lastError = null;
  cdnStatus.forcedOffline = false;
  console.log('[CDN FALLBACK] CDN status reset');
}
